import { array, mixed, number, object, string, type InferType } from 'yup-v1-latest';

export const EXTERNAL_ID_CATALOG_SCHEMA_VERSION = 1;

const externalIDCatalogRootKeys = new Set(['schemaVersion', 'types']);
const externalIDTypeKeys = new Set(['key', 'displayLabel', 'maxLength', 'restrictOrgEdit', 'restrictOrgRemove']);

const externalIDTypeSchema = object({
  key: string()
    .transform((_value, originalValue: unknown) =>
      typeof originalValue === 'string' ? originalValue.trim() : originalValue
    )
    .required('Key is required.')
    .matches(/^[A-Za-z0-9._-]+$/, 'Key may contain only letters, numbers, dots, underscores, and hyphens.'),
  displayLabel: string()
    .transform((_value, originalValue: unknown) =>
      typeof originalValue === 'string' ? originalValue.trim() : originalValue
    )
    .optional(),
  maxLength: number()
    .transform((_value, originalValue: unknown) => originalValue)
    .integer('maxLength must be an integer.')
    .positive('maxLength must be positive.')
    .nullable()
    .optional(),
  restrictOrgEdit: mixed<boolean>()
    .test(
      'restrictOrgEdit-boolean',
      'restrictOrgEdit must be a boolean.',
      value => value === undefined || typeof value === 'boolean'
    )
    .optional(),
  restrictOrgRemove: mixed<boolean>()
    .test(
      'restrictOrgRemove-boolean',
      'restrictOrgRemove must be a boolean.',
      value => value === undefined || typeof value === 'boolean'
    )
    .optional(),
}).test('known-properties', 'External ID types contain an unknown property.', value =>
  Object.keys(value).every(key => externalIDTypeKeys.has(key))
);

export const externalIDTypesConfigurationSchema = array()
  .of(externalIDTypeSchema)
  .required()
  .test('unique-keys', 'External ID type keys must be unique case-insensitively.', value => {
    const normalizedKeys = value.map(type => (typeof type?.key === 'string' ? type.key.toLowerCase() : type?.key));
    return new Set(normalizedKeys).size === normalizedKeys.length;
  });

export const externalIDCatalogDocumentSchema = object({
  schemaVersion: number()
    .required('schemaVersion is required.')
    .oneOf([EXTERNAL_ID_CATALOG_SCHEMA_VERSION], 'schemaVersion must be 1.'),
  types: externalIDTypesConfigurationSchema,
})
  .required()
  .test('known-properties', 'External ID catalog contains an unknown property.', value =>
    Object.keys(value).every(key => externalIDCatalogRootKeys.has(key))
  );

export type ExternalIDType = InferType<typeof externalIDTypeSchema>;
export type ExternalIDTypes = ExternalIDType[];
export type ExternalIDCatalogDocument = InferType<typeof externalIDCatalogDocumentSchema>;

export type ExternalIDTypesConfigurationState = {
  externalIDTypes: ExternalIDTypes;
  isActive: boolean;
  isInvalid: boolean;
};

function parseExternalIDCatalogDocument(value: unknown): ExternalIDCatalogDocument | undefined {
  try {
    return externalIDCatalogDocumentSchema.validateSync(value, {
      abortEarly: false,
    });
  } catch {
    return undefined;
  }
}

export function parseExternalIDTypesFromCatalogValue(value: unknown): ExternalIDTypes | undefined {
  const document = parseExternalIDCatalogDocument(value);
  return document?.types;
}

export function parseExternalIDTypesConfiguration(value: string | undefined): ExternalIDTypesConfigurationState {
  if (value == null || value === '') {
    return {
      externalIDTypes: [],
      isActive: false,
      isInvalid: false,
    };
  }

  let parsedValue: unknown;
  try {
    parsedValue = JSON.parse(value);
  } catch {
    return {
      externalIDTypes: [],
      isActive: false,
      isInvalid: true,
    };
  }

  const externalIDTypes = parseExternalIDTypesFromCatalogValue(parsedValue);
  if (externalIDTypes == null) {
    return {
      externalIDTypes: [],
      isActive: false,
      isInvalid: true,
    };
  }

  return {
    externalIDTypes,
    isActive: true,
    isInvalid: false,
  };
}

export type ExternalIDEntityLabels = {
  entityLabelSingular: string;
  entityLabelPlural: string;
};

export const EXTERNAL_ID_ENTITY_LABELS = {
  Counterparty: { entityLabelSingular: 'Counterparty', entityLabelPlural: 'Counterparties' },
} as const satisfies Record<string, ExternalIDEntityLabels>;

export function getExternalIDTypeRemovalWarning(labels: ExternalIDEntityLabels): string {
  return `Removing this External ID type removes the configured field from all ${labels.entityLabelPlural} editors. It does not delete existing ${labels.entityLabelSingular} values. Existing values will remain visible under the machine key as unconfigured External IDs.`;
}

export function getExternalIDTypesUpdateWarning(
  labels: ExternalIDEntityLabels,
  savedValue: unknown,
  proposedValue: unknown
): string | undefined {
  const savedTypes = parseExternalIDTypesFromCatalogValue(savedValue);
  const proposedTypes = parseExternalIDTypesFromCatalogValue(proposedValue);
  if (savedTypes == null || proposedTypes == null) {
    return undefined;
  }

  const proposedKeys = new Set(proposedTypes.map(type => type.key));
  const removed = savedTypes.some(type => !proposedKeys.has(type.key));
  if (!removed) {
    return undefined;
  }

  return getExternalIDTypeRemovalWarning(labels);
}

export function createDefaultExternalIDCatalogDocument(types: ExternalIDTypes): ExternalIDCatalogDocument {
  return {
    schemaVersion: EXTERNAL_ID_CATALOG_SCHEMA_VERSION,
    types,
  };
}

export function serializeExternalIDCatalogDocument(types: ExternalIDTypes): string {
  return JSON.stringify(createDefaultExternalIDCatalogDocument(types));
}

export const EXTERNAL_ID_CATALOG_FORMATTED_DEFAULT_VALUE = JSON.stringify(
  createDefaultExternalIDCatalogDocument([
    { key: '', displayLabel: '', maxLength: null, restrictOrgEdit: true, restrictOrgRemove: true },
  ]),
  null,
  2
);

export function createExternalIDCatalogOrgConfig<TKey extends `${string}.${string}`>(
  key: TKey,
  labels: ExternalIDEntityLabels
) {
  return {
    key,
    description: `Defines ${labels.entityLabelSingular} External ID catalog types (schemaVersion 1 envelope). Each type supports key, optional displayLabel, optional maxLength, and optional restrictOrgEdit / restrictOrgRemove flags for org-admin catalog management.`,
    valueType: 'json',
    flagType: 'configuration',
    schema: externalIDCatalogDocumentSchema,
    formattedDefaultValue: EXTERNAL_ID_CATALOG_FORMATTED_DEFAULT_VALUE,
    getUpdateWarning: (savedValue: unknown, proposedValue: unknown) =>
      getExternalIDTypesUpdateWarning(labels, savedValue, proposedValue),
  } as const;
}
